import { useRef, useState } from "react";
import type { AvatarMaterialDto, DeliverableDto, DeliverableKind } from "../../shared/dto";
import { WalkFeedback, type WalkPoint } from "../WalkFeedback";
import { AutomationTimingEstimate } from "../AutomationTimingEstimate";
import { DocOverlay, docMetaLine, docTileCtaKey, walkPointFromEvent } from "../doc/DocOverlay";
import { MarkdownView } from "../doc/MarkdownView";
import { ReaderApprovalAction } from "../doc/walk";
import { EmailsCopyView } from "./EmailsSection";
import { DeliverableNotes, FeedbackComposer } from "./Notes";
import { ReviewLoopIntro, humanDocTitle, type FocusedSalesKey } from "./shared";

// Interface-document card (audit, plan, emails, webinar): Launchpad-designed
// rich document with a clamped preview, full-screen reading, confirm (gate
// kinds) and comments typed or spoken. Only the sales page keeps the client's
// own brand design (Pavel 2026-07-23).
export function GateDocCard(props: {
  kind: DeliverableKind;
  dataKind?: string;
  deliverable: DeliverableDto;
  canAct: boolean;
  canApprove: boolean;
  ctaKey?: string;
  approvalHintKey?: string;
  showActionHint?: boolean;
  displayKicker?: string;
  displayTitle?: string;
  busyAction: string | null;
  csrfToken?: string;
  /** F104: the partner's written reason when they returned the material. */
  staffReturnedReason?: string | null;
  t: (key: string) => string;
  onApprove: () => void;
  onFeedback: (payload: { text?: string | null; audioAttachmentId?: string | null; pointerTrace?: WalkPoint[] }) => Promise<boolean>;
}) {
  const item = props.deliverable;
  const [reading, setReading] = useState(false);
  const [walkActive, setWalkActive] = useState(false);
  const [notesVersion, setNotesVersion] = useState(0);
  const [readProgress, setReadProgress] = useState<{ key: string; percent: number }>({ key: "", percent: 0 });
  const [readThroughKey, setReadThroughKey] = useState<string | null>(null);
  const walkPointerRef = useRef<((point: Omit<WalkPoint, "t">) => void) | null>(null);
  const approving = props.busyAction === `gate-approve:${item.id}`;
  const sending = props.busyAction === `gate-feedback:${item.id}`;
  const readerKey = `${item.id}:${item.version}`;
  const approvalUnlocked = readThroughKey === readerKey;
  const currentProgress = readProgress.key === readerKey ? readProgress.percent : 0;
  const approvalHintKey = props.approvalHintKey ?? "sales.gate.hint";
  const showApproval = props.canAct && props.canApprove && item.status === "review";
  const reviewMaterial: FocusedSalesKey = props.kind === "emails" || props.kind === "webinar"
    ? props.kind
    : "strategy";

  // ONE mic in the panel (Pavel 2026-07-23): the walk orb is the only voice
  // entry, the composer below is typed-only, saved notes stack underneath.
  const actions = props.canAct && item.status === "review" && (
    <div className="gate-doc-actions">
      {props.canApprove && (
        <ReaderApprovalAction
          placement="panel"
          unlocked={approvalUnlocked}
          progress={currentProgress}
          approving={approving}
          sending={sending}
          hintKey={approvalHintKey}
          t={props.t}
          onApprove={props.onApprove}
        />
      )}
      {props.csrfToken && (
        <WalkFeedback
          csrfToken={props.csrfToken}
          t={props.t}
          pointerRef={walkPointerRef}
          onRecordingChange={setWalkActive}
          draft={{ surface: "deliverable", targetId: props.kind }}
          onSubmit={async (payload) => {
            const delivered = await props.onFeedback(payload);
            if (delivered) setNotesVersion((value) => value + 1);
            return delivered;
          }}
        />
      )}
      <FeedbackComposer
        t={props.t}
        draftKey={props.kind}
        sending={sending}
        onSubmit={async (payload) => {
          const delivered = await props.onFeedback(payload);
          if (delivered) setNotesVersion((value) => value + 1);
          return delivered;
        }}
      />
      {props.csrfToken && <DeliverableNotes deliverableId={item.id} csrfToken={props.csrfToken} t={props.t} version={notesVersion} hasDoc />}
    </div>
  );

  return (
    <article
      className="deliverable-card doc-tile"
      data-deliverable-kind={props.dataKind ?? props.kind}
      data-comments-locked={item.status === "approved" ? "true" : undefined}
    >
      {/* 🔴 K15: the step page's header already carries the state pill, and on
          the strategy step the two said the same thing in the same amber 280 px
          apart ("NA ŘADĚ" over "KE KONTROLE"). One state, one pill, and it
          belongs to the page, which is the only place that can also speak for
          the locked and the waiting variants. The slot the duplicate vacated is
          exactly where the version belongs: beside the name of the material it
          numbers. It used to hang at the far end of the action row, ~430 px of
          emptiness from the button it had nothing to do with. */}
      <div className="deliverable-card-heading">
        <div>
          <p className="card-kicker">{props.displayKicker ?? props.t(`sales.kind.${props.kind}`)}</p>
          <h2>{props.displayTitle ?? humanDocTitle(item.title, props.t(`sales.kind.${props.kind}`))}</h2>
        </div>
        {item.contentMd && (
          <small className="version-pill">{props.t("sales.version").replace("{version}", String(item.version))}</small>
        )}
      </div>

      {/* F104: honest state on the material itself. A return says WHY in the
          partner's own words; an approval made on a call says who confirmed
          it. Both gate on the LIVE status, so a stale note cannot lie. */}
      {item.status === "review" && props.staffReturnedReason && (
        <p className="message staff-returned-note" role="status">
          {props.t("sales.gate.staffReturned").replace("{reason}", props.staffReturnedReason)}
        </p>
      )}
      {item.status === "approved" && item.approvedByStaff && (
        <p className="message success-message staff-approved-note" role="status">
          {props.t("sales.gate.staffApprovedNote")}
        </p>
      )}

      {item.contentMd ? (
        <>
          {/* Fixed per-kind blurb in OUR voice. The document lead used to sit
              here, but generated docs VYKAJI by decision (Pavel 26.7.) while
              the UI tyka - the blurb keeps the two voices out of one screen. */}
          <p className="doc-tile-excerpt">{props.t(`sales.tileBlurb.${props.kind}`)}</p>
          <div className="doc-tile-actions">
            <button className="primary-button" type="button" onClick={() => setReading(true)}>
              {props.t(props.ctaKey ?? docTileCtaKey(item.status, props.canAct))}
            </button>
          </div>
          {props.showActionHint !== false && props.canAct && props.canApprove && item.status === "review" && (
            <p className="doc-tile-hint">{props.t("sales.doc.actionHint")}</p>
          )}
        </>
      ) : (
        <div className="deliverable-empty"><span className="loading-pulse" /><p>{props.t(`sales.preparing.${props.kind}`)}</p></div>
      )}

      {reading && item.contentMd && (
        <DocOverlay
          title={item.title}
          kicker={props.t(`sales.kind.${props.kind}`)}
          meta={docMetaLine(props.t, item.version, item.updatedAt)}
          closeLabel={props.t("sales.doc.close")}
          t={props.t}
          panelLabel={props.t("sales.doc.panelLabel")}
          onClose={() => setReading(false)}
          walkActive={walkActive}
          intro={showApproval
            ? <ReviewLoopIntro material={reviewMaterial} placement="reader" t={props.t} />
            : undefined}
          printable
          onReadProgress={showApproval
            ? (state) => {
              setReadProgress((previous) => {
                const previousPercent = previous.key === readerKey ? previous.percent : 0;
                const percent = Math.max(previousPercent, state.percent);
                return previous.key === readerKey && previous.percent === percent
                  ? previous
                  : { key: readerKey, percent };
              });
              if (state.atEnd) setReadThroughKey(readerKey);
            }
            : undefined}
          onContentPointerMove={(event) => walkPointerRef.current?.(walkPointFromEvent(event))}
          aside={item.status === "approved"
            ? (
              <div className="gate-doc-actions">
                {/* Approval of the STRATEGY starts the rest of the production
                    line, so say that instead of talking about locked comments
                    (Pavel 2026-08-03). The later materials have nothing
                    downstream, so they keep the neutral wording. */}
                <p className="message success-message" role="status">
                  {props.t(["audit", "plan"].includes(String(item.kind))
                    ? "sales.gate.approvedStrategyNote"
                    : "sales.gate.approvedNote")}
                </p>
                {/* The conversation stays readable after approval: the notes
                    are the record of what was agreed and worked in. */}
                {props.csrfToken && <DeliverableNotes deliverableId={item.id} csrfToken={props.csrfToken} t={props.t} version={notesVersion} hasDoc />}
              </div>
            )
            : actions || undefined}
        >
          {/* Emails read as copy-ready sections (D12); every other document
              stays the plain reader. */}
          {props.kind === "emails"
            ? <EmailsCopyView md={item.contentMd} t={props.t} />
            : <MarkdownView md={item.contentMd} />}
          {showApproval && (
            <ReaderApprovalAction
              placement="end"
              unlocked={approvalUnlocked}
              progress={currentProgress}
              approving={approving}
              sending={sending}
              hintKey={approvalHintKey}
              t={props.t}
              onApprove={props.onApprove}
            />
          )}
        </DocOverlay>
      )}
    </article>
  );
}

// The generated AI avatar as a sales material (F18, Pavel 2026-07-24:
// "prodejni material je avatar"): image tile in the grid, TRUE fullscreen
// view through the shared DocOverlay panel system, and a download link.
// Renders only honest states - the image itself, or "preparing" once the
// client's photo exists and generation is still pending.
export function AvatarCard(props: { material: AvatarMaterialDto; t: (key: string) => string }) {
  const [viewing, setViewing] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  const ready = props.material.state === "ready" && Boolean(props.material.url);
  return (
    <article className="deliverable-card avatar-card" data-deliverable-kind="avatar">
      <div className="deliverable-card-heading">
        <div>
          <p className="card-kicker">{props.t("sales.kind.avatar")}</p>
          <h2>{ready ? props.t("sales.avatarTitle") : props.t("sales.preparingTitle")}</h2>
        </div>
        {ready && <span className="status-pill pill-done">{props.t("sales.avatarReady")}</span>}
      </div>
      {!ready ? (
        <div className="deliverable-empty">
          <span className="loading-pulse" />
          <p>{props.t("sales.preparing.avatar")}</p>
          <AutomationTimingEstimate stats={props.material.automationStats} t={props.t} />
        </div>
      ) : (
        <>
          <button className={`preview-shot-button avatar-shot-button${avatarLoaded ? " is-loaded" : ""}`} type="button" onClick={() => setViewing(true)} aria-label={props.t("sales.avatarView")}>
            <img
              className="preview-shot avatar-shot"
              src={props.material.url}
              alt={props.t("sales.avatarTitle")}
              loading="lazy"
              decoding="async"
              onLoad={() => setAvatarLoaded(true)}
              // A cache-complete image can skip onLoad after hydration.
              ref={(el) => { if (el?.complete && el.naturalWidth > 0) setAvatarLoaded(true); }}
            />
          </button>
          <div className="doc-tile-actions">
            <button className="primary-button" type="button" onClick={() => setViewing(true)}>{props.t("sales.avatarView")}</button>
            <a className="quiet-link" href={props.material.url} download>{props.t("sales.avatarDownload")}</a>
          </div>
        </>
      )}
      {viewing && ready && (
        <DocOverlay
          title={props.t("sales.avatarTitle")}
          kicker={props.t("sales.kind.avatar")}
          closeLabel={props.t("sales.doc.close")}
          t={props.t}
          onClose={() => setViewing(false)}
          panelLabel={props.t("sales.doc.panelLabel")}
        >
          <div className="avatar-full">
            <img src={props.material.url} alt={props.t("sales.avatarTitle")} />
            <a className="primary-button compact-button" href={props.material.url} download>{props.t("sales.avatarDownload")}</a>
          </div>
        </DocOverlay>
      )}
    </article>
  );
}
